import { fetchEventSource } from '@microsoft/fetch-event-source'
import { SESSION_EXPIRED, CHANGE_TOKEN} from 'actionTypes'
import User from 'utils/User'

const config = require('../../../config-env.json')

const ENV = process.env.API_ENV
const configEnv = config[ENV]
export const API = configEnv.API_URL
export const { API_KEY } = process.env
const APPLICATION_CODE = 1
const user = new User()
const INTERNAL_API = 'v1/mobile'

/*
 * Class to deal with API requests
 */
class ApiRequest {
  constructor(token, dispatch, metadata, suppressSessionExpired = false) {
    this.token = token
    this.dispatch = dispatch
    this.metadata = metadata
    this.suppressSessionExpired = suppressSessionExpired
  }


  postFormData(endpoint,params) {
    return fetch(endpoint, {
      method: 'POST',
      headers: {Authorization: this.token},
      body: params,
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleMetadata(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  putFormData(endpoint,params) {
    return fetch(endpoint, {
      method: 'PUT',
      headers: {Authorization: this.token, apiKey: API_KEY},
      body: params,
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleMetadata(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  /**
   * POST Request
   * @param {String} endpoint = API endpoint
   * @params {Object} params = params to be sent to API
   * @return {Promise} response
   */
  post(endpoint, params, signal = false) {
    if (!signal) {
      const abortController = new AbortController()
      signal = abortController.signal
    }
    return fetch(endpoint, {
      method: 'POST',
      headers: this.headers(endpoint),
      body: JSON.stringify(params),
      signal,
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleMetadata(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }


  eventSourceStream(endpoint,query, headers, signalController, onMessage, onClose, onError) {
  const queryParams = this.createQueryParams(query)


    if (queryParams) {
      endpoint = `${endpoint}?${queryParams}`
    }

    let response = {}
    return fetchEventSource(endpoint, {
      openWhenHidden: true,
      method: "GET",
      headers: {
        ...this.headers(endpoint),
        headers,
        Accept: "text/event-stream",
         // 'content-type':'text/event-stream'
      },
      signal: signalController.signal,
      onmessage(event) {
        const parseData = JSON.parse(event.data)
        response = parseData
        if(parseData && parseData.data !== false) onMessage(parseData)
      },
      onclose() {
        onClose()
      },
      onerror(err) {
        signalController.abort()
        const status = err?.status || err?.response?.status
        if (status === 401 || status === 403) {
          const authError = new Error('Unauthorized')
          authError.status = status
          throw authError
        }
        onError(err)
        throw new Error()
      },
    })
    .then(() => this.checkExpiredSession(response))
    .then(() => this.updateToken(response));
  }

  /**
   * GET Request
   * @param {String} endpoint = API endpoint
   * @return {Promise} response
   */
  get(endpoint, query, headers) {
    const queryParams = this.createQueryParams(query)

    if (queryParams) {
      endpoint = `${endpoint}?${queryParams}`
    }

    return fetch(endpoint, {
      method: 'GET',
      headers: {
        ...this.headers(endpoint),
        ...headers
      },
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleMetadata(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  getFile(endpoint) {
    return fetch(endpoint, {
      method: 'GET',
      headers: this.headers(endpoint)
    }).then((response) => response)
  }

  /**
   * PUT Request
   * @param {String} endpoint = API endpoint
   * @params {Object} params = params to be sent to API
   * @return {Promise} response
   */
  put(endpoint, params) {
    return fetch(endpoint, {
      method: 'PUT',
      headers: this.headers(endpoint),
      body: JSON.stringify(params),
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleMetadata(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  deleteWithBody(endpoint, params) {
    

    return fetch(endpoint, {
      method: 'DELETE',
      headers: this.headers(endpoint),
      body: JSON.stringify(params)
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  delete(endpoint, query) {
    const queryParams = this.createQueryParams(query)

    if (queryParams) {
      endpoint = `${endpoint}?${queryParams}`
    }

    return fetch(endpoint, {
      method: 'DELETE',
      headers: this.headers(endpoint),
    })
      .then((response) => this.checkExpiredSession(response))
      .then((response) => this.handleData(response))
      .then((response) => this.handleResponse(response))
      .then((response) => this.updateToken(response))
  }

  /**
   * Sets request headers
   * @return {Object} headers
   */
  headers(endpoint) {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'lm-application': APPLICATION_CODE,
    }

    if(endpoint.includes(INTERNAL_API)) {
      headers.Apikey = API_KEY
    }

    if (this.token) {
      headers.Authorization = this.token

      const [, naPrograma] = window.location.pathname.split('/')

      // Sets current route name
      headers.naPrograma = naPrograma
    }

    if (this.metadata) {
      headers._offset = this.metadata.page
      headers._limit = this.metadata.size
    }

    return headers
  }


  /**
   * Checks response status
   * @return {Boolean}
   */
  checkStatus(response) {
    const status = response.cdStatus

    return status >= 200 && status < 299
  }

  /**
   * Sets http status code in the body if necessary
   * @return {Promise} response
   */
  handleData = async (response) => {
    const responseData = await response.json()

    if (!responseData.cdStatus) {
      responseData.cdStatus = response.status
    }

    return responseData
  }

  /**
   * Request's errors handling
   * @return {Promise} response
   */
  handleResponse(response) {
    if (response.data || this.checkStatus(response)) {
      if (this.responseMetadata) {
        response.metadata = this.responseMetadata
      }

      return response
    }

    return Promise.reject(response)
  }

  /*
   * Creates query params
   * @param query {Object}
   * @return {String} query
   * e.g:
   * query = { name: 'username', age: '18' }
   * queryParams 'name=username&age=18'
   */
  createQueryParams(query) {
    if (!query || Object.keys(query).length < 1) {
      return
    }

    const esc = encodeURIComponent

    const queryParams = Object.keys(query)
      .map((k) => `${esc(k)}=${esc(query[k])}`)
      .join('&')

    return queryParams
  }

  /**
   * Handles expired session
   * @return {Promise} response
   */
  checkExpiredSession(response) {
    if (response.status === 401 || response.status === 403 || response.cdStatus === 401 || response.cdStatus === 403) {
      if (this.suppressSessionExpired) {
        return Promise.reject(response)
      }
      this.dispatch({ type: SESSION_EXPIRED })
      user.clearCache()
    }

    return response
  }


  handleMetadata(response) {
    const offset = response.headers.get('_offset')
    const limit = response.headers.get('_limit')
    const pages = response.headers.get('pages')
    const total = response.headers.get('total')

    if (offset && limit && pages && total) {
      this.responseMetadata = {
        pageCount: parseInt(pages, 10),
        page: parseInt(offset, 10),
        totalCount: parseInt(total, 10),
        size: parseInt(limit, 10),
      }
    }

    return response
  }

  updateToken(response) {
    if (response.dsToken && this.dispatch) {
        this.token = response.dsToken
      if (user.currentUser) {
        const newUser = user.currentUser
        newUser.dsToken = response.dsToken
        user.currentUser = newUser
      }

      this.dispatch({
        type: CHANGE_TOKEN,
        payload: response.dsToken,
      })
    }

    return response
  }
}

export default ApiRequest